/**
 * ADO #307 — Curated Java built-in classes available in the Class Instance
 * resource form. Phase 2 pedagogical scope: the most useful classes a
 * beginning student will hit when writing realistic Java problems.
 *
 * Shared by:
 *   - AddResourceModalV2/index.jsx → dropdown options
 *   - utils/codeGenerator.js → JAVA_BUILTIN_IMPORTS lookup for auto-import
 *
 * Each entry:
 *   name     Bare class name as the student types and as it appears in
 *            generated code (`StringBuilder`, `ArrayList`).
 *   package  Full Java package. `java.lang` entries need NO import; every
 *            other package gets a single dedupe'd `import …;` line at the
 *            top of the generated file.
 *   role     One-line pedagogical hint shown in the dropdown. Helps the
 *            student pick the right collection without prior Java exposure.
 *
 * Explicitly NOT in the Phase 2 set (per the ADO #307 spec):
 *   - Scanner   → auto-injected by Input nodes; instantiating it manually
 *                 duplicates that behaviour and confuses students.
 *   - Math      → static-only utility; instantiation is meaningless.
 *   - String    → primitive-style in the Variable form already.
 *   - Thread, Runnable → advanced track.
 *   - File / BufferedReader / FileReader / PrintWriter → Phase 3+ I/O.
 *   - Exception types (IllegalArgumentException, etc.) → constructed
 *     inline by the Throw node (ADO #304), not declared as instance slots.
 */
/**
 * ADO #310 — Each entry now carries a `constructors` array. The
 * InstantiateNodeEditor reads these when the target's class is a
 * built-in (skipping the Contract Flow lookup that only applies to
 * project-defined classes). One entry per overload, signature shape
 * mirrors how the Contract Flow stores `constructor_decl.parameters`
 * so the same arg-rendering code works for both sources.
 *
 * Phase 2 curated set — favours the no-arg / capacity / seed forms a
 * beginning student will reach for. Phase 3+ adds collection-copy
 * constructors when generics land.
 */
export const JAVA_BUILTIN_CLASSES = [
  // ── java.lang (no import needed) ────────────────────────────────────
  { name: 'StringBuilder', package: 'java.lang',
    role: 'Mutable string — efficient append in loops',
    constructors: [
      { params: [] },
      { params: [{ type: 'String', name: 'initial' }] },
      { params: [{ type: 'int', name: 'capacity' }] },
    ] },
  { name: 'Integer', package: 'java.lang',
    role: 'Boxed int — needed for ArrayList<Integer> etc.',
    constructors: [
      { params: [{ type: 'int', name: 'value' }] },
    ] },
  { name: 'Double', package: 'java.lang',
    role: 'Boxed double — boxed-primitive sibling of Integer',
    constructors: [
      { params: [{ type: 'double', name: 'value' }] },
    ] },

  // ── java.util (require an import line) ──────────────────────────────
  { name: 'ArrayList', package: 'java.util',
    role: 'Resizable list — the workhorse collection',
    constructors: [
      { params: [] },
      { params: [{ type: 'int', name: 'initialCapacity' }] },
    ] },
  { name: 'LinkedList', package: 'java.util',
    role: 'Doubly-linked list — efficient inserts at ends',
    constructors: [
      { params: [] },
    ] },
  { name: 'HashMap', package: 'java.util',
    role: 'Key→value map (unordered keys)',
    constructors: [
      { params: [] },
      { params: [{ type: 'int', name: 'initialCapacity' }] },
    ] },
  { name: 'TreeMap', package: 'java.util',
    role: 'Key→value map with sorted keys',
    constructors: [
      { params: [] },
    ] },
  { name: 'HashSet', package: 'java.util',
    role: 'Set of unique values (unordered)',
    constructors: [
      { params: [] },
      { params: [{ type: 'int', name: 'initialCapacity' }] },
    ] },
  { name: 'TreeSet', package: 'java.util',
    role: 'Set of unique values with sorted order',
    constructors: [
      { params: [] },
    ] },
  { name: 'Random', package: 'java.util',
    role: 'Random number generator',
    constructors: [
      { params: [] },
      { params: [{ type: 'long', name: 'seed' }] },
    ] },
];

/**
 * Fast-lookup map from bare class name → full package. Empty string means
 * java.lang (no import line needed). Used by codegen's auto-import path.
 *
 * Example:
 *   JAVA_BUILTIN_PACKAGE_BY_NAME['ArrayList']    → 'java.util'
 *   JAVA_BUILTIN_PACKAGE_BY_NAME['StringBuilder']→ 'java.lang'
 *   JAVA_BUILTIN_PACKAGE_BY_NAME['Dog']          → undefined (project class)
 */
export const JAVA_BUILTIN_PACKAGE_BY_NAME = Object.freeze(
  JAVA_BUILTIN_CLASSES.reduce((acc, c) => {
    acc[c.name] = c.package;
    return acc;
  }, {}),
);

/**
 * Resolve the import needed for a given class.
 *
 * Returns:
 *   - null if the class is in java.lang (no import needed)
 *   - null if the class is unknown (e.g. project class — caller handles it)
 *   - `<package>.<ClassName>` string otherwise (the FQCN to import)
 *
 * For Custom-typed classes the modal stores the FQCN on the resource as
 * `classFqcn`; codegen prefers that over this lookup.
 */
export function resolveJavaImportForClass(className) {
  const pkg = JAVA_BUILTIN_PACKAGE_BY_NAME[className];
  if (!pkg || pkg === 'java.lang') return null;
  return `${pkg}.${className}`;
}

/**
 * ADO #310 — Return the curated constructors for a built-in class, or
 * null when the class is unknown (project class / custom / typo).
 * Shape matches Contract Flow `constructor_decl.parameters` so the
 * InstantiateNodeEditor renders both sources with the same code:
 *
 *   getBuiltinConstructors('ArrayList')
 *     → [{ params: [] }, { params: [{ type: 'int', name: 'initialCapacity' }] }]
 *
 *   getBuiltinConstructors('Dog') → null
 */
export function getBuiltinConstructors(className) {
  const entry = JAVA_BUILTIN_CLASSES.find((c) => c.name === className);
  return entry ? entry.constructors : null;
}

/**
 * ADO #310 — Convenience predicate the InstantiateNodeEditor uses to
 * branch its constructor-source lookup. A class is "built-in" if it
 * appears in the curated catalog (independent of how the resource was
 * saved — covers both `classOrigin: 'builtin'` and legacy resources
 * created before ADO #307's origin field).
 */
export function isJavaBuiltinClass(className) {
  return Object.prototype.hasOwnProperty.call(JAVA_BUILTIN_PACKAGE_BY_NAME, className);
}

export default {
  JAVA_BUILTIN_CLASSES,
  JAVA_BUILTIN_PACKAGE_BY_NAME,
  resolveJavaImportForClass,
  getBuiltinConstructors,
  isJavaBuiltinClass,
};
